import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Megaphone, ArrowRight } from 'lucide-react';
import EditableText from './EditableText';
import { useEditMode } from '../context/EditModeContext';
import { RADIUS_PILL } from '../config/brand';

const STORAGE_KEY = 'announcementBannerDismissed';

function setBannerPresent(on) {
  document.documentElement.classList.toggle('has-announcement-banner', on);
}

function setBannerHeightCss(px) {
  document.documentElement.style.setProperty(
    '--announcement-banner-height',
    px == null ? '0px' : `${Math.max(0, Math.round(px))}px`
  );
}

const AnnouncementBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef(null);
  const { isEditMode } = useEditMode();

  useEffect(() => {
    if (!sessionStorage.getItem(STORAGE_KEY)) {
      setIsVisible(true);
    }
  }, []);

  // En mode édition, toujours afficher la bannière pour pouvoir la modifier
  useEffect(() => {
    if (isEditMode) setIsVisible(true);
  }, [isEditMode]);

  // Hauteur intrinsèque (sans inset) → navbar + padding stables au scroll
  useLayoutEffect(() => {
    if (!isVisible) {
      setBannerPresent(false);
      setBannerHeightCss(null);
      return undefined;
    }

    const el = bannerRef.current;
    if (!el) {
      setBannerPresent(false);
      setBannerHeightCss(null);
      return undefined;
    }

    setBannerPresent(true);
    const update = () => setBannerHeightCss(Math.ceil(el.offsetHeight));

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    el.addEventListener('transitionend', update);
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
      el.removeEventListener('transitionend', update);
      setBannerPresent(false);
      setBannerHeightCss(null);
    };
  }, [isVisible]);

  const handleDismiss = () => {
    if (isEditMode) return;
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible) return null;

  return (
    <div
      ref={bannerRef}
      className="site-announcement fixed z-[110]"
      role="region"
      aria-label="Annonce"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-2 sm:gap-3">
        <Megaphone className="h-4 w-4 shrink-0 opacity-90 hidden sm:block" />
        <p className="text-sm font-medium flex-1 leading-snug min-w-0">
          <EditableText
            editKey="announcement.text"
            defaultValue="Stages et ateliers ouverts"
            as="span"
            className="text-sm font-medium"
          />
        </p>
        <Link
          to="/contact"
          className={`inline-flex items-center gap-1 text-xs font-semibold bg-white/20 hover:bg-white/30 ${RADIUS_PILL} px-2.5 sm:px-3 py-1 transition-colors shrink-0`}
        >
          <EditableText
            editKey="announcement.link"
            defaultValue="Nous contacter"
            as="span"
            className="text-xs font-semibold"
          />
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
        {!isEditMode && (
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 rounded-md text-white/80 hover:text-white hover:bg-white/15 transition-colors shrink-0"
            aria-label="Fermer l'annonce"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementBanner;
